import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { avatarSrc } from "../lib/api";
import SearchBox from "./SearchBox";
import NotificationBell from "./NotificationBell";
import AnnouncementBar from "./AnnouncementBar";
import { SiteTheme } from "./SiteTheme";
import { PushPrompt } from "./PushControls";
import InstallPrompt from "./InstallPrompt";
import {
  Archive as ArchiveIcon, Home, Compass, PlusSquare, MessageCircle, Flame, User, Shield, LogOut,
  Link2, HardDrive, Users, Upload as UploadIcon, Hash, Menu, UserPlus, Globe, Bookmark, MailWarning, X, Wand2, BookOpen, Sparkles, Network, Heart,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

function Avatar({ src, name, size = 36, testid }) {
  if (src) return <img data-testid={testid} src={avatarSrc(src)} alt={name} style={{ width: size, height: size }} className="rounded-full object-cover bg-[#161922]" />;
  return (
    <div data-testid={testid} style={{ width: size, height: size }} className="rounded-full bg-[#0066FF] flex items-center justify-center text-white font-semibold">
      {name?.[0]?.toUpperCase() || "?"}
    </div>
  );
}

function HeaderIcon({ to, icon: Icon, label, active, badge }) {
  return (
    <NavLink
      to={to}
      data-testid={`nav-${label.toLowerCase()}`}
      aria-label={label}
      className="relative w-14 h-11 rounded-lg flex items-center justify-center transition-colors hover:bg-white/[0.06]"
    >
      <Icon size={23} className={active ? "text-[#0066FF]" : "text-[#9CA3AF]"} strokeWidth={active ? 2.4 : 2} />
      {active && <span className="absolute bottom-0 left-2 right-2 h-[3px] bg-[#0066FF] rounded-full" />}
      {badge > 0 && (
        <span data-testid="nav-messages-badge" className="absolute top-1 right-2 min-w-[18px] h-[18px] px-1 bg-[#E60023] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </NavLink>
  );
}

function SidebarItem({ to, icon: Icon, label, testid, accent = "#0066FF", onClick }) {
  const loc = useLocation();
  const [path, query] = to.split("?");
  const queryMatch = (isActive) => {
    if (!isActive) return false;
    const cur = new URLSearchParams(loc.search);
    if (query) return [...new URLSearchParams(query)].every(([k, v]) => cur.get(k) === v);
    return !(path === "/explore" && cur.get("feed") === "following");
  };
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={testid}
      end={to === "/"}
      className={({ isActive: rawActive }) => { const isActive = queryMatch(rawActive); return (
        `group relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
          isActive ? "bg-[#0066FF]/[0.12] text-white" : "text-[#9CA3AF] hover:bg-white/[0.05] hover:text-white"
        }`); }
      }
    >
      {({ isActive: rawActive }) => { const isActive = queryMatch(rawActive); return (
        <>
          <span
            className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r-full transition-opacity duration-200"
            style={{ background: accent, opacity: isActive ? 1 : 0 }}
          />
          <Icon size={20} style={{ color: isActive ? accent : undefined }} className="shrink-0" />
          <span className="text-[15px] font-semibold tracking-tight truncate">{label}</span>
        </>
      ); }}
    </NavLink>
  );
}

function NavSection({ label }) {
  return (
    <div className="flex items-center gap-2 px-3 mt-5 mb-1.5 first:mt-1">
      <span className="font-mono text-[9px] font-bold uppercase tracking-[0.28em] text-[#54586a]">{label}</span>
      <span className="h-px flex-1 bg-white/[0.06]" />
    </div>
  );
}

function LeftSidebar({ onNavigate }) {
  const { user } = useAuth();
  const [tags, setTags] = useState([]);
  useEffect(() => {
    api.get("/tags/popular", { params: { limit: 8 } }).then(({ data }) => setTags(data)).catch(() => {});
  }, []);
  return (
    <nav className="flex flex-col gap-0.5 px-3 py-4">
      {user && (
        <Link
          to={`/u/${user.username}`}
          onClick={onNavigate}
          data-testid="sidebar-profile"
          className="flex items-center gap-3 px-2 py-2.5 mb-2 rounded-xl hover:bg-white/[0.05] transition-colors"
        >
          <Avatar src={user.avatar_url} name={user.username} size={40} />
          <div className="min-w-0">
            <p className="text-white font-bold text-[15px] leading-tight truncate">{user.display_name || user.username}</p>
            <p className="text-[#6B7280] text-xs truncate">@{user.username}</p>
          </div>
        </Link>
      )}

      <NavSection label="Discover" />
      <SidebarItem to="/" icon={Home} label="Home" testid="sidebar-home" accent="#0066FF" onClick={onNavigate} />
      <SidebarItem to="/explore" icon={Compass} label="Explore" testid="sidebar-explore" accent="#F1A817" onClick={onNavigate} />
      {user && <SidebarItem to="/foryou" icon={Sparkles} label="For You" testid="sidebar-foryou" accent="#00E5FF" onClick={onNavigate} />}
      <SidebarItem to="/members" icon={Users} label="Members" testid="sidebar-members" accent="#31A24C" onClick={onNavigate} />
      <SidebarItem to="/discover" icon={Wand2} label="AI Discover" testid="sidebar-discover" accent="#00E5FF" onClick={onNavigate} />

      <NavSection label="Archive" />
      <SidebarItem to="/archive" icon={ArchiveIcon} label="Archive" testid="sidebar-archive" accent="#00E5FF" onClick={onNavigate} />
      <SidebarItem to="/concepts" icon={Network} label="Concepts" testid="sidebar-concepts" accent="#A855F7" onClick={onNavigate} />
      <SidebarItem to="/stories" icon={BookOpen} label="Stories" testid="sidebar-stories" accent="#00E5FF" onClick={onNavigate} />
      {user && <SidebarItem to="/saved" icon={Bookmark} label="Saved" testid="sidebar-saved" accent="#00E5FF" onClick={onNavigate} />}
      {user && <SidebarItem to="/explore?feed=following" icon={Users} label="Following" testid="sidebar-following" accent="#31A24C" onClick={onNavigate} />}
      <SidebarItem to="/donate" icon={Heart} label="Support KinkVault" testid="sidebar-donate" accent="#FF2A5F" onClick={onNavigate} />

      {user && <NavSection label="Create" />}
      {user && <SidebarItem to="/upload" icon={UploadIcon} label="Upload" testid="sidebar-upload" accent="#E60023" onClick={onNavigate} />}
      {user && <SidebarItem to="/import" icon={Link2} label="Import from URL" testid="sidebar-import" accent="#A855F7" onClick={onNavigate} />}
      {user && <SidebarItem to="/import?mode=drive" icon={HardDrive} label="Google Drive" testid="sidebar-drive" accent="#14B8A6" onClick={onNavigate} />}
      {user && <SidebarItem to="/chat" icon={MessageCircle} label="Messages" testid="sidebar-messages" accent="#0066FF" onClick={onNavigate} />}

      {user && (user.role === "admin" || user.role === "moderator") && (
        <>
          <NavSection label="Admin" />
          <SidebarItem to="/admin" icon={Shield} label={user.role === "admin" ? "Admin" : "Moderator"} testid="sidebar-admin" accent="#E60023" onClick={onNavigate} />
        </>
      )}

      {tags.length > 0 && (
        <>
          <NavSection label="Trending" />
          <div className="flex flex-wrap gap-2 px-2 pt-1">
            {tags.map((t) => (
              <Link key={t.tag} to={`/explore?tag=${t.tag}`} onClick={onNavigate} data-testid={`sidebar-tag-${t.tag}`} className="px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/[0.06] text-[#C9CAD1] text-xs font-medium hover:bg-white/10 hover:text-white transition-colors flex items-center gap-1">
                <Hash size={11} className="text-[#4599FF]" />{t.tag}
              </Link>
            ))}
          </div>
        </>
      )}
    </nav>
  );
}

const BOTTOM = [
  { to: "/", icon: Home, label: "Home", key: "home" },
  { to: "/explore", icon: Compass, label: "Explore", key: "explore" },
  { to: "/archive", icon: ArchiveIcon, label: "Archive", key: "archive" },
  { to: "/upload", icon: PlusSquare, label: "Upload", key: "upload", center: true, auth: true },
  { to: "/chat", icon: MessageCircle, label: "Messages", key: "chat", auth: true, badge: true },
];

function MobileBottomNav({ unread }) {
  const { user } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const items = [...BOTTOM, { to: user ? `/u/${user.username}` : "/login", icon: User, label: "Profile", key: "profile" }];
  return (
    <nav data-testid="mobile-bottom-nav" className="md:hidden fixed bottom-0 inset-x-0 safe-bottom-nav bg-[#0B0C10] border-t border-white/[0.07] z-50 flex justify-around items-center">
      {items.map((it) => {
        const active = it.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(it.to.split("?")[0]) && it.to !== "/";
        const go = (e) => { if (it.auth && !user) { e.preventDefault(); nav("/login"); } };
        if (it.center) {
          return (
            <Link key={it.key} to={it.to} onClick={go} data-testid={`bottom-nav-${it.key}`} className="w-12 h-12 -mt-2 rounded-full bg-[#0066FF] flex items-center justify-center text-white shadow-[0_6px_20px_rgba(0,102,255,0.5)] active:scale-95 transition-transform">
              <PlusSquare size={24} />
            </Link>
          );
        }
        return (
          <Link key={it.key} to={it.to} onClick={go} data-testid={`bottom-nav-${it.key}`} className="relative flex flex-col items-center justify-center w-14 h-full">
            <it.icon size={22} className={active ? "text-[#0066FF]" : "text-[#8A8D99]"} />
            {it.badge && unread > 0 && (
              <span className="absolute top-1.5 right-2 min-w-[16px] h-4 px-1 bg-[#E60023] text-white text-[9px] font-bold rounded-full flex items-center justify-center">{unread > 99 ? "99+" : unread}</span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}

function SuggestedPeople() {
  const { user } = useAuth();
  const [people, setPeople] = useState([]);
  const [followed, setFollowed] = useState({});
  const [busy, setBusy] = useState({});
  useEffect(() => {
    api.get("/users/suggested", { params: { limit: 5 } }).then(({ data }) => setPeople(data || [])).catch(() => {});
  }, []);
  const follow = async (p) => {
    if (!user) { window.location.href = "/login"; return; }
    setBusy((b) => ({ ...b, [p.id]: true }));
    try {
      await api.post(`/users/${p.id}/follow`);
      setFollowed((f) => ({ ...f, [p.id]: true }));
    } catch { /* noop */ }
    finally { setBusy((b) => ({ ...b, [p.id]: false })); }
  };
  const visible = people.filter((p) => !followed[p.id]);
  if (visible.length === 0) return null;
  return (
    <div className="bg-[#0F1117] border border-white/[0.07] rounded-2xl p-5" data-testid="rail-suggested-people">
      <h3 className="text-white font-heading text-base tracking-tight mb-4 flex items-center gap-2"><Users size={16} className="text-[#0066FF]" /> People to follow</h3>
      <div className="flex flex-col gap-3">
        {visible.map((p) => (
          <div key={p.id} className="flex items-center gap-2" data-testid={`suggested-user-${p.id}`}>
            <Link to={`/u/${p.username}`} className="shrink-0">
              <Avatar src={p.avatar_url} name={p.username} size={38} />
            </Link>
            <Link to={`/u/${p.username}`} className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-[#FFFFFF] truncate hover:underline">{p.display_name}</p>
              <p className="text-[11px] text-[#6B7280] truncate">{p.followers} follower{p.followers === 1 ? "" : "s"} · {p.posts} post{p.posts === 1 ? "" : "s"}</p>
            </Link>
            <button data-testid={`suggested-follow-${p.id}`} onClick={() => follow(p)} disabled={busy[p.id]} className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#0066FF] hover:bg-[#0055CC] disabled:opacity-50 text-white text-xs font-semibold transition-colors">
              <UserPlus size={13} /> Follow
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RightRail() {
  const [tags, setTags] = useState([]);
  useEffect(() => {
    api.get("/tags/popular", { params: { limit: 14 } }).then(({ data }) => setTags(data)).catch(() => {});
  }, []);
  return (
    <div className="flex flex-col gap-4 py-4">
      <SuggestedPeople />
      <div className="bg-[#0F1117] border border-white/[0.07] rounded-2xl p-5">
        <h3 className="text-white font-heading text-base tracking-tight mb-4 flex items-center gap-2"><Flame size={16} className="text-[#FF2A5F]" /> Trending kinks</h3>
        <div className="flex flex-wrap gap-2">
          {tags.length === 0 && <span className="text-[#6B7280] text-sm">Nothing trending yet.</span>}
          {tags.map((t) => (
            <Link key={t.tag} to={`/explore?tag=${t.tag}`} data-testid={`rail-tag-${t.tag}`} className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.07] text-[#F3F4F6] text-xs font-medium hover:bg-white/10 hover:border-[#0066FF]/40 transition-colors flex items-center gap-1">
              #{t.tag}<span className="text-[#6B7280]">{t.count}</span>
            </Link>
          ))}
        </div>
      </div>
      <div className="bg-[#0F1117] border border-white/[0.07] rounded-2xl overflow-hidden">
        <AdSlot />
      </div>
      <p className="px-2 text-[11px] text-[#6B7280] leading-relaxed">
        <Link to="/about" data-testid="rail-about-link" className="text-[#7fe9ff] hover:text-white transition-colors">KinkVault</Link> · a living archive of queer erotic history · 18+ only · all content is community-submitted.
      </p>
    </div>
  );
}

function AdSlot() {
  const [AdBanner, setAdBanner] = useState(null);
  useEffect(() => { import("./AdBanner").then((m) => setAdBanner(() => m.default)); }, []);
  if (!AdBanner) return <div className="min-h-[100px]" />;
  return <AdBanner zone="1121861" width={300} height={250} />;
}

function Header({ unread }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [drawer, setDrawer] = useState(false);
  const p = loc.pathname;
  const doLogout = async () => { await logout(); nav("/"); };
  return (
    <>
    <header data-testid="app-header" className="sticky top-0 z-50 safe-header bg-[#0B0C10]/95 backdrop-blur-md border-b border-white/[0.07] flex items-center gap-2 px-3 sm:px-4">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <button data-testid="mobile-menu-btn" onClick={() => setDrawer(true)} className="lg:hidden w-10 h-10 rounded-full hover:bg-white/[0.06] flex items-center justify-center text-[#F3F4F6]"><Menu size={22} /></button>
        <Link to="/" data-testid="logo-home-link" className="group flex items-center gap-2 shrink-0">
          <span className="w-9 h-9 rounded-xl bg-[#0066FF] flex items-center justify-center shadow-[0_4px_16px_rgba(0,102,255,0.5)] transition-transform duration-200 group-hover:scale-105"><Flame size={19} className="text-white" fill="white" /></span>
          <span className="hidden sm:inline font-heading text-xl tracking-tight text-[#FFFFFF]">Kink<span className="text-[#0066FF]">Vault</span></span>
        </Link>
        <div className="hidden sm:block max-w-[280px] w-full ml-2"><SearchBox variant="nav" /></div>
      </div>

      <nav className="hidden md:flex items-center gap-1 flex-1 justify-center max-w-[420px]">
        <HeaderIcon to="/" icon={Home} label="Home" active={p === "/"} />
        <HeaderIcon to="/explore" icon={Compass} label="Explore" active={p.startsWith("/explore")} />
        <HeaderIcon to="/archive" icon={ArchiveIcon} label="Archive" active={p.startsWith("/archive")} />
        {user && <HeaderIcon to="/upload" icon={PlusSquare} label="Upload" active={p.startsWith("/upload")} />}
        {user && <HeaderIcon to="/chat" icon={MessageCircle} label="Messages" active={p.startsWith("/chat")} badge={unread} />}
      </nav>

      <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
        <Link to="/donate" data-testid="header-donate" className="hidden sm:flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#FF2A5F] hover:bg-[#e01e50] text-white font-semibold transition-colors text-sm shadow-[0_4px_16px_rgba(255,42,95,0.4)]">
          <Heart size={15} fill="white" /> Support
        </Link>
        {user ? (
          <>
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="account-menu-trigger" className="outline-none rounded-full focus:ring-2 focus:ring-[#0066FF]">
              <Avatar src={user.avatar_url} name={user.username} size={38} testid="account-avatar" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#0F1117] border-white/10 text-[#F3F4F6] w-56 shadow-[0_12px_28px_rgba(0,0,0,0.6)]">
              <DropdownMenuItem asChild>
                <Link to={`/u/${user.username}`} data-testid="menu-profile" className="cursor-pointer focus:bg-white/[0.06]"><User size={16} className="mr-2" /> Your Profile</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/upload" data-testid="menu-upload" className="cursor-pointer focus:bg-white/[0.06]"><UploadIcon size={16} className="mr-2" /> Upload</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/import" data-testid="menu-import" className="cursor-pointer focus:bg-white/[0.06]"><Link2 size={16} className="mr-2" /> Import</Link>
              </DropdownMenuItem>
              {(user.role === "admin" || user.role === "moderator") && (
                <DropdownMenuItem asChild>
                  <Link to="/admin" data-testid="menu-admin" className="cursor-pointer focus:bg-white/[0.06]"><Shield size={16} className="mr-2" /> {user.role === "admin" ? "Admin panel" : "Moderator"}</Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem data-testid="menu-logout" onClick={doLogout} className="cursor-pointer focus:bg-white/[0.06] text-[#E60023]"><LogOut size={16} className="mr-2" /> Log out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </>
        ) : (
          <>
            <Link to="/login" data-testid="header-login" className="px-4 py-2 rounded-lg text-[#F3F4F6] font-semibold hover:bg-white/[0.06] transition-colors text-sm">Log in</Link>
            <Link to="/register" data-testid="header-join" className="px-4 py-2 rounded-lg bg-[#0066FF] hover:bg-[#0055CC] text-white font-semibold transition-colors text-sm">Join</Link>
          </>
        )}
      </div>
    </header>
    {drawer && (
      <div className="lg:hidden fixed inset-0 z-[100]" data-testid="mobile-drawer">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDrawer(false)} />
        <div className="absolute left-0 top-0 bottom-0 w-[280px] safe-pt bg-[#0B0C10] border-r border-white/10 shadow-[0_0_60px_rgba(0,0,0,0.9)] overflow-y-auto animate-in slide-in-from-left duration-200">
          <div className="h-14 flex items-center px-4 border-b border-white/[0.06]">
            <span className="font-heading text-lg text-white">Kink<span className="text-[#0066FF]">Vault</span></span>
          </div>
          <LeftSidebar onNavigate={() => setDrawer(false)} />
        </div>
      </div>
    )}
    </>
  );
}

function VerifyBanner() {
  const { user, refresh } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  if (!user || user.email_verified || dismissed) return null;

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent — check your inbox");
    } catch {
      toast.error("Couldn't send email. Try again shortly.");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="verify-banner" className="bg-[#00E5FF]/10 border-b border-[#00E5FF]/30">
      <div className="max-w-[1600px] mx-auto px-4 py-2.5 flex items-center gap-3 text-sm">
        <MailWarning size={17} className="text-[#00E5FF] shrink-0" />
        <span className="text-[#E4E6EB] flex-1 min-w-0">Confirm your email to secure your account.</span>
        <button data-testid="verify-resend" onClick={resend} disabled={sending} className="text-[#00E5FF] hover:text-white font-semibold whitespace-nowrap disabled:opacity-50">
          {sending ? "Sending…" : "Resend link"}
        </button>
        <button data-testid="verify-dismiss" onClick={() => setDismissed(true)} className="text-[#6B7280] hover:text-white shrink-0" aria-label="Dismiss"><X size={16} /></button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let alive = true;
    const poll = () => api.get("/conversations-unread/count").then(({ data }) => alive && setUnread(data.unread || 0)).catch(() => {});
    poll();
    const t = setInterval(poll, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  return (
    <div className="min-h-screen">
      <AnnouncementBar />
      <SiteTheme />
      <PushPrompt />
      <Header unread={unread} />
      <VerifyBanner />
      <div className="flex">
        <aside className="hidden lg:block w-[256px] xl:w-[268px] shrink-0 sticky safe-sticky-rail bg-[#0B0C10] border-r border-white/[0.07] overflow-y-auto custom-scrollbar">
          <LeftSidebar />
        </aside>
        <main className="relative z-[1] flex-1 min-w-0 pb-20 md:pb-8 safe-main-pb">{children}</main>
      </div>
      <MobileBottomNav unread={unread} />
      <InstallPrompt />
    </div>
  );
}
